import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, ArrowLeft, CalendarDays, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useDayEvents } from "@/hooks/useDayEvents";
import { useNow } from "@/hooks/useNow";
import { useOnline } from "@/hooks/useOnline";
import {
  toISODate,
  parseISODate,
  formatLongDate,
  todayISO,
  isToday,
  pad2,
} from "@/lib/time";
import { computeOverlapSegments } from "@/lib/overlap";
import { classifyHorizontalGesture, dayDeltaForDir, nextScaleIndex } from "@/lib/calendarGestures";
import TimelineBar from "@/components/TimelineBar";
import EventDetailDialog from "@/components/EventDetailDialog";
import OverlapDialog from "@/components/OverlapDialog";
import GoToDateDialog from "@/components/GoToDateDialog";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "@/i18n";

const DEFAULT_DAY_START_HOUR = 8;

export default function Calendar() {
  const { date } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const safeDate = date || todayISO();

  const events = useDayEvents(safeDate);
  const overlapSegments = useMemo(() => computeOverlapSegments(events), [events]);
  const eventsById = useMemo(
    () => Object.fromEntries(events.map((e) => [e.id, e])),
    [events]
  );

  const now = useNow(30 * 1000);
  const online = useOnline();
  const showNow = isToday(safeDate);
  const nowMinutes = showNow ? now.getHours() * 60 + now.getMinutes() : null;
  const scrollToMinutes = showNow
    ? now.getHours() * 60 + now.getMinutes()
    : DEFAULT_DAY_START_HOUR * 60;

  const [activeEvent, setActiveEvent] = useState(null);
  const [activeOverlap, setActiveOverlap] = useState(null);
  const [goToOpen, setGoToOpen] = useState(false);
  const [resolution, setResolution] = useState(() => {
    const saved = Number(localStorage.getItem("chronicles.calendar.resolution"));
    // Default to the 8 × 1-hour view; only honour a valid previously saved choice.
    // (Legacy "480" / 8-hour choices fall back to the default.)
    return [15, 60, 120, 1440].includes(saved) ? saved : 60;
  });

  const onSetResolution = (mins) => {
    setResolution(mins);
    localStorage.setItem("chronicles.calendar.resolution", String(mins));
    window.dispatchEvent(new CustomEvent("chronicles:settings-changed"));
  };

  const barSubtitle = t(`calendar.barSubtitle.${resolution}`);
  const barTitle = t(`calendar.barTitle.${resolution}`);

  const shiftDay = (delta) => {
    const d = parseISODate(safeDate);
    d.setDate(d.getDate() + delta);
    navigate(`/calendar/${toISODate(d)}`);
  };

  const SCALES = [15, 60, 120, 1440];
  // Slow horizontal drag steps the calendar scale (clamped at the ends).
  const shiftScale = (dir) => {
    const idx = SCALES.indexOf(resolution);
    if (idx === -1) return;
    const next = nextScaleIndex(idx, dir, SCALES.length);
    if (next !== idx) onSetResolution(SCALES[next]);
  };

  // --- Mobile horizontal gestures (additive to the on-screen buttons) ---------
  // A slow, controlled horizontal drag changes the SCALE; a fast horizontal swipe
  // changes the DAY. Classification combines distance, velocity, duration and the
  // horizontal:vertical ratio (see lib/calendarGestures). Vertical scrolling and
  // taps are untouched — we never preventDefault here, and a day/scale change fires
  // only for a clearly-horizontal gesture that does NOT begin on a protected/
  // interactive element.
  const SWIPE_MAX_MS = 900;      // reject very slow drags entirely (likely hesitation)
  const PROTECTED_SEL = 'button, a, input, textarea, select, [role="dialog"], [contenteditable="true"], [data-noswipe], [data-testid="resolution-tabs"]';
  const swipe = useRef({ x: 0, y: 0, t: 0, active: false });
  const mainRef = useRef(null);
  // Keep the latest handlers (they close over date/resolution) reachable from the
  // native listeners attached once below.
  const gestureRef = useRef({ shiftDay, shiftScale });
  gestureRef.current = { shiftDay, shiftScale };

  // Attach the swipe listeners NATIVELY (addEventListener) rather than via React
  // synthetic props. React 18 event delegation isn't reliably reached by some
  // programmatic/synthetic touch dispatch; native listeners fire consistently on
  // real touch devices AND automated touch contexts. We never preventDefault, so
  // vertical scrolling and taps stay fully reliable.
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return undefined;

    const onStart = (e) => {
      const touches = e.touches;
      if (touches && touches.length !== 1) { swipe.current.active = false; return; }
      const point = touches ? touches[0] : e; // touch or pointer
      const target = e.target;
      if (target && target.closest && target.closest(PROTECTED_SEL)) { swipe.current.active = false; return; }
      swipe.current = { x: point.clientX, y: point.clientY, t: Date.now(), active: true };
    };
    const onEnd = (e) => {
      const s = { ...swipe.current };   // snapshot before we reset the shared ref
      swipe.current.active = false;
      if (!s.active) return;
      const point = (e.changedTouches && e.changedTouches[0]) || e;
      if (!point || point.clientX == null) return;
      const dx = point.clientX - s.x;
      const dy = point.clientY - s.y;
      const dt = Date.now() - s.t;
      if (dt > SWIPE_MAX_MS) return;                  // too slow → not an intentional gesture
      const g = classifyHorizontalGesture({ dx, dy, dt });
      if (!g) return;                                 // tap / vertical / diagonal → ignore
      if (g.type === "day") gestureRef.current.shiftDay(dayDeltaForDir(g.dir));
      else gestureRef.current.shiftScale(g.dir);
    };
    const onCancel = () => { swipe.current.active = false; };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", onCancel, { passive: true });
    // Pointer fallback (pen/touch) for environments/devices where touch events
    // aren't delivered; mouse is excluded so desktop clicks never change the day.
    const onPointerDown = (e) => { if (e.pointerType && e.pointerType !== "mouse") onStart(e); };
    const onPointerUp = (e) => { if (e.pointerType && e.pointerType !== "mouse") onEnd(e); };
    el.addEventListener("pointerdown", onPointerDown, { passive: true });
    el.addEventListener("pointerup", onPointerUp, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onCancel);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointerup", onPointerUp);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-3xl px-4 sm:px-5 py-2 sm:py-3 flex items-center gap-2">
          <Link
            to="/"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100 transition"
            data-testid="calendar-back"
            aria-label={t("calendar.backToHome")}
          >
            <ArrowLeft className="h-4 w-4 text-slate-700" />
          </Link>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
              {showNow ? t("calendar.today") : t("calendar.dayView")}
            </p>
            <p
              className="font-display text-base sm:text-lg font-semibold text-slate-900 truncate"
              data-testid="calendar-date-label"
            >
              {formatLongDate(safeDate)}
            </p>
          </div>
          <LanguageSwitcher />
          <button
            onClick={() => setGoToOpen(true)}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100 transition"
            data-testid="calendar-goto-date"
            aria-label={t("calendar.pickAnotherDate")}
          >
            <CalendarDays className="h-4 w-4 text-slate-700" />
          </button>
        </div>

        {/* Day nav */}
        <div className="mx-auto max-w-3xl px-4 sm:px-5 pb-2 sm:pb-3 flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-9 px-3 border-slate-300"
            onClick={() => shiftDay(-1)}
            data-testid="calendar-prev-day"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> {t("calendar.prev")}
          </Button>
          {!showNow ? (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full h-9 px-3 text-slate-700"
              onClick={() => navigate(`/calendar/${todayISO()}`)}
              data-testid="calendar-jump-today"
            >
              {t("calendar.jumpToToday")}
            </Button>
          ) : (
            <span className="text-xs font-mono-tab text-slate-500" data-testid="calendar-now">
              {t("calendar.now")} {pad2(now.getHours())}:{pad2(now.getMinutes())}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-9 px-3 border-slate-300"
            onClick={() => shiftDay(1)}
            data-testid="calendar-next-day"
          >
            {t("calendar.next")} <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </header>

      <main
        ref={mainRef}
        className="flex-1 mx-auto w-full max-w-3xl px-4 sm:px-5 py-3 sm:py-5 space-y-3 sm:space-y-4 pb-24 sm:pb-28"
        data-testid="calendar-swipe-area"
      >
        {/* Resolution selector */}
        <div className="flex gap-1.5 rounded-full bg-slate-100 p-1 w-full overflow-x-auto" data-testid="resolution-tabs">
          {[15, 60, 120, 1440].map((mins) => {
            const active = resolution === mins;
            return (
              <button
                key={mins}
                onClick={() => onSetResolution(mins)}
                className={[
                  "flex-1 h-9 rounded-full text-xs font-medium transition shrink-0",
                  active ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900",
                ].join(" ")}
                data-testid={`resolution-${mins}`}
              >
                {t(`calendar.resolutions.${mins}`)}
              </button>
            );
          })}
        </div>

        <div key={`${safeDate}-${resolution}`} className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200">
          <TimelineBar
            title={barTitle}
            subtitle={barSubtitle}
            resolutionMinutes={resolution}
            events={events}
            overlapSegments={overlapSegments}
            onEventClick={(ev) => navigate(`/edit/${ev.id}`)}
            onOverlapClick={setActiveOverlap}
            nowMinutes={nowMinutes}
            scrollToMinutes={scrollToMinutes}
            testIdPrefix={`bar-${resolution}`}
          />
        </div>
      </main>

      {/* Floating create */}
      <button
        onClick={() => navigate(`/create/${safeDate}`)}
        className="fixed bottom-6 left-5 sm:bottom-8 sm:left-auto sm:right-8 h-14 px-5 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 active:scale-95 transition-all inline-flex items-center gap-2 z-40"
        data-testid="calendar-fab-create"
      >
        <Plus className="h-5 w-5" />
        <span className="font-medium">{t("calendar.create")}</span>
      </button>

      {/* Floating AI assistant — placed top-of-stack on mobile (above Create) so it never sits under the Emergent badge at bottom-right */}
      <button
        onClick={() => {
          if (!online) {
            toast.error(t("home.aiOfflineToastTitle"), {
              description: t("home.aiOfflineToastDesc"),
            });
            return;
          }
          navigate("/assistant", { state: { mode: "assistant" } });
        }}
        className="fixed bottom-24 left-5 sm:bottom-8 sm:left-auto sm:right-32 h-14 w-14 rounded-full bg-white border border-slate-300 text-slate-900 shadow-md hover:bg-slate-50 active:scale-95 transition-all inline-flex items-center justify-center z-40"
        data-testid="calendar-fab-assistant"
        data-ai-offline={!online}
        aria-label={online ? t("calendar.openAssistant") : t("calendar.assistantOffline")}
      >
        <Sparkles className={online ? "h-5 w-5" : "h-5 w-5 opacity-40"} />
        {!online ? (
          <span
            className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center ring-2 ring-white"
            data-testid="calendar-assistant-lock"
            aria-hidden
          >
            <Lock className="h-2.5 w-2.5" strokeWidth={3} />
          </span>
        ) : null}
      </button>

      <EventDetailDialog
        open={!!activeEvent}
        onOpenChange={(o) => !o && setActiveEvent(null)}
        event={activeEvent}
      />
      <OverlapDialog
        open={!!activeOverlap}
        onOpenChange={(o) => !o && setActiveOverlap(null)}
        segment={activeOverlap}
        eventsById={eventsById}
      />
      <GoToDateDialog open={goToOpen} onOpenChange={setGoToOpen} initialDate={safeDate} />
    </div>
  );
}
